import { type Maybe } from "@booking/types";
import pluralize from "pluralize";

import type { Course, Offer } from "./types";

export const allocatedRoundsCountForCourse = (
  course: Course,
  offer: Offer,
): number =>
  offer.roundAllocation.allocated.reduce(
    (sum, round) => sum + (round.course.id === course.id ? round.rounds : 0),
    0,
  );

export const boardBasis = ({ accommodation }: Offer): string =>
  accommodation[0]?.boardBasis;

export const coursesOnOffer = (offer?: Offer): Course[] => [
  ...(offer?.roundAllocation.allocated.map((round) => round.course) || []),
  ...(offer?.roundAllocation.unallocated?.courses || []),
  ...(offer?.roundAllocation.unlimited || []),
];

export const hotelNames = ({ accommodation }: Offer): string =>
  accommodation.map(({ hotel }) => hotel).join(", ");

export const isCourseUnallocated = (course: Course, offer: Offer): boolean =>
  offer.roundAllocation.unallocated?.courses.some((c) => c.id === course.id) ||
  false;

export const isCourseUnlimited = (course: Course, offer: Offer): boolean =>
  offer.roundAllocation.unlimited.some((c) => c.id === course.id);

export const nightsOnOffer = (offer?: Offer): Maybe<number> =>
  offer?.accommodation.reduce((total, accom) => total + accom.nights, 0) ||
  null;

export const shouldRemoveEnquiry = (
  totalPax: number,
  minLowPax: number,
): boolean => {
  return totalPax < minLowPax;
};

export const roomName = ({ accommodation }: Offer): string =>
  accommodation[0]?.roomName;

export const roundsOnOffer = (offer?: Offer): Maybe<number> => {
  const nights = nightsOnOffer(offer);

  if (!offer || !nights) {
    return null;
  }

  const allocated = offer.roundAllocation.allocated.reduce(
    (total, round) => total + round.rounds,
    0,
  );
  const unallocated = offer.roundAllocation.unallocated?.rounds || 0;
  const unlimited = offer.roundAllocation.unlimited.length > 0 ? nights + 1 : 0;

  return allocated + unallocated + unlimited;
};

export const displayNightsOnOffer = (offer?: Offer): string => {
  const nights = nightsOnOffer(offer);

  if (!offer || !nights) {
    return "";
  }

  return pluralize("night", nights, true);
};

export const displayRoundsOnOffer = (offer?: Offer): string => {
  const rounds = roundsOnOffer(offer);

  if (!offer || !rounds) {
    return "";
  }

  if (offerHasUnlimitedRounds(offer)) {
    return "Unlimited rounds";
  } else {
    return pluralize("round", rounds, true);
  }
};

export const offerHasUnlimitedRounds = (offer?: Offer): boolean => {
  if (!offer) {
    return false;
  }

  return !!offer.roundAllocation.unlimited.length;
};
